import axios from 'axios';
import { Hope, WishEstatePrice } from '@/domain/hope.entity';

// eslint-disable-next-line no-shadow
export enum HopeContentType {
  actual = 1,
  history = 2,
}

export interface GetHopeForm {
  contentType: HopeContentType,
  nowDate?: Date,
  ohEventTypeId?: number,
  limit?: number,
  hopeIds?: number[],
}

interface GetHopeResponse {
  hopeId: string,
  fcShopName: string,
  fcShopId: string,
  encodedShopId: string,
  hopePrefName: string,
  hopePrefId: string,
  hopeCityName1: string,
  hopeCityName2: string,
  hopeCityName3: string,
  hopeCityId1: string | null,
  hopeCityId2: string | null,
  hopeCityId3: string | null,
  newEstateFlag: boolean,
  newEstatePriceMin: string,
  newEstatePriceMax: string,
  newEstatePriceMinFormatted?: string,
  newEstatePriceMaxFormatted?: string,
  newEstatePriceMinUnit?: string,
  newEstatePriceMaxUnit?: string,
  oldEstateFlag: boolean,
  oldEstatePriceMin: string,
  oldEstatePriceMax: string,
  oldEstatePriceMinFormatted?: string,
  oldEstatePriceMaxFormatted?: string,
  oldEstatePriceMinUnit?: string,
  oldEstatePriceMaxUnit?: string,
  newMansionFlag: boolean,
  newMansionPriceMin: string,
  newMansionPriceMax: string,
  newMansionPriceMinFormatted?: string,
  newMansionPriceMaxFormatted?: string,
  newMansionPriceMinUnit?: string,
  newMansionPriceMaxUnit?: string,
  oldMansionFlag: boolean,
  oldMansionPriceMin: string,
  oldMansionPriceMax: string,
  oldMansionPriceMinFormatted?: string,
  oldMansionPriceMaxFormatted?: string,
  oldMansionPriceMinUnit?: string,
  oldMansionPriceMaxUnit?: string,
  landFlag: boolean,
  landPriceMin: string,
  landPriceMax: string,
  landPriceMinFormatted?: string,
  landPriceMaxFormatted?: string,
  landPriceMinUnit?: string,
  landPriceMaxUnit?: string,
  businessFlag: boolean,
  businessPriceMin: string,
  businessPriceMax: string,
  businessPriceMinFormatted: string,
  businessPriceMaxFormatted: string,
  businessPriceMinUnit: string,
  businessPriceMaxUnit: string,
  shinyabinServiceFlag: string,
  shinyabinFlag: string,
  allowedSendEmail: boolean,
  promotional: boolean,
  newsletter: boolean,
}

export interface CreteManyHopeForm {
  fcShopId: number,
  prefId: number,
  cityId1: number,
  cityId2?: number,
  cityId3?: number,
  typeId: string,

  estateType: {
    newEstatePrice: boolean,
    oldEstatePrice: boolean,
    newMansionPrice: boolean,
    oldMansionPrice: boolean,
    landPrice: boolean,
    businessPrice: boolean,
  },

  newEstatePrice: [number, number],
  oldEstatePrice: [number, number],
  newMansionPrice: [number, number],
  oldMansionPrice: [number, number],
  landPrice: [number, number],
  businessPrice: [number, number],

  pushShinyabinFlag: boolean,
  newstellerFlag: boolean,
}

export interface UpdateManyHopeForm{
  id: number,
  fcShopId: number,
  typeId: string,
  newEstatePrice: [number, number] | null,
  oldEstatePrice: [number, number] | null,
  newMansionPrice: [number, number] | null,
  oldMansionPrice: [number, number] | null,
  landPrice: [number, number] | null,
  businessPrice: [number, number] | null,
  pushShinyabinFlag: boolean,
}

export enum DeletePlaceFlag {
  MYPAGE = 1,
  ALL = 2,
}

export enum RemoveWishAbortReasonTypesHttp {
  BuyOtherHouse = 0,
  DontHaveInformation = 1,
  StopToSearching = 2,
  Others = 3,
}

export interface UpdateHopeForm {
  id: number,
  fcShopId: number,
  prefId: number,
  cityId1: number,
  cityId2?: number,
  cityId3?: number,

  estateType: {
    newEstatePrice: boolean,
    oldEstatePrice: boolean,
    newMansionPrice: boolean,
    oldMansionPrice: boolean,
    landPrice: boolean,
    businessPrice: boolean,
  },

  newEstatePrice: [number, number],
  oldEstatePrice: [number, number],
  newMansionPrice: [number, number],
  oldMansionPrice: [number, number],
  landPrice: [number, number],
  businessPrice: [number, number],

  shinyabinFlag: boolean,
  sinyaBinServiceFlag: boolean,

  deleteFlag?: boolean,
  deleteType?: 'ALL' | 'MYPAGE',
  deleteReasonExplanation?: string,
  deleteReason?: string,
  type?: RemoveWishAbortReasonTypesHttp,
  allowEmailService?: boolean,
  newHouseService?: boolean,
  emailMagazineService?: boolean,
}

interface RecoverHopeForm {
  id: number,
}

export default {
  async get(form: GetHopeForm): Promise<Hope[]> {
    const params: any = { contentType: form.contentType };
    if (form.hopeIds && form.hopeIds.length > 0) {
      params.hopeIds = form.hopeIds.join(',');
    }
    const response = await axios.get('/hopes', { params });
    const data = response.data as GetHopeResponse[];
    return data.map((item) => ({
      id: Number(item.hopeId),
      fcShop: {
        id: Number(item.fcShopId),
        name: item.fcShopName,
        encodedShopId: item.encodedShopId,
      },
      pref: {
        id: Number(item.hopePrefId),
        name: item.hopePrefName,
      },
      cities: [{
        id: Number(item.hopeCityId1),
        name: item.hopeCityName1,
      }, {
        id: Number(item.hopeCityId2),
        name: item.hopeCityName2,
      }, {
        id: Number(item.hopeCityId3),
        name: item.hopeCityName3,
      }].filter((city) => city.id),
      newEstatePrice: item.newEstateFlag ? { min: Number(item.newEstatePriceMin || 0) as WishEstatePrice, max: Number(item.newEstatePriceMax || 999999)  as WishEstatePrice} : undefined,
      oldEstatePrice: item.oldEstateFlag ? { min :Number(item.oldEstatePriceMin || 0) as WishEstatePrice, max: Number(item.oldEstatePriceMax || 999999) as WishEstatePrice} : undefined,
      newMansionPrice: item.newMansionFlag ? { min :Number(item.newMansionPriceMin || 0) as WishEstatePrice, max: Number(item.newMansionPriceMax || 999999) as WishEstatePrice} : undefined,
      oldMansionPrice: item.oldMansionFlag ? { min: Number(item.oldMansionPriceMin || 0) as WishEstatePrice, max: Number(item.oldMansionPriceMax || 999999) as WishEstatePrice} : undefined,
      landPrice: item.landFlag ? { min: Number(item.landPriceMin || 0) as WishEstatePrice, max: Number(item.landPriceMax || 999999) as WishEstatePrice} : undefined,
      businessPrice: item.businessFlag ? { min: Number(item.businessPriceMin || 0) as WishEstatePrice, max: Number(item.businessPriceMax || 999999) as WishEstatePrice} : undefined,
      newEstatePriceFormatted: item.newEstateFlag ? { min: item.newEstatePriceMinFormatted || '0', max: item.newEstatePriceMaxFormatted || '999999' } : undefined,
      oldEstatePriceFormatted: item.oldEstateFlag ? { min: item.oldEstatePriceMinFormatted || '0', max: item.oldEstatePriceMaxFormatted || '999999' } : undefined,
      newMansionPriceFormatted: item.newMansionFlag ? { min: item.newMansionPriceMinFormatted || '0', max: item.newMansionPriceMaxFormatted || '999999' } : undefined,
      oldMansionPriceFormatted: item.oldMansionFlag ? { min: item.oldMansionPriceMinFormatted || '0', max: item.oldMansionPriceMaxFormatted || '999999' } : undefined,
      landPriceFormatted: item.landFlag ? { min: item.landPriceMinFormatted || '0', max: item.landPriceMaxFormatted || '999999' } : undefined,
      businessPriceFormatted: item.businessFlag ? { min: item.businessPriceMinFormatted || '0', max: item.businessPriceMaxFormatted || '999999' } : undefined,
      newEstatePriceUnit: item.newEstateFlag ? { min: item.newEstatePriceMinUnit || '0', max: item.newEstatePriceMaxUnit || '999999' } : undefined,
      oldEstatePriceUnit: item.oldEstateFlag ? { min: item.oldEstatePriceMinUnit || '0', max: item.oldEstatePriceMaxUnit || '999999' } : undefined,
      newMansionPriceUnit: item.newMansionFlag ? { min: item.newMansionPriceMinUnit || '0', max: item.newMansionPriceMaxUnit || '999999' } : undefined,
      oldMansionPriceUnit: item.oldMansionFlag ? { min: item.oldMansionPriceMinUnit || '0', max: item.oldMansionPriceMaxUnit || '999999' } : undefined,
      landPriceUnit: item.landFlag ? { min: item.landPriceMinUnit || '0', max: item.landPriceMaxUnit || '999999' } : undefined,
      businessPriceUnit: item.businessFlag ? { min: item.businessPriceMinUnit || '0', max: item.businessPriceMaxUnit || '999999' } : undefined,
      shinyabinServiceFlag: Boolean(Number(item.shinyabinServiceFlag)),
      shinyabinFlag: Boolean(Number(item.shinyabinFlag)),
      emailConsents: {
        allowedSendEmail: Boolean(Number(item.allowedSendEmail)),
        promotional: Boolean(Number(item.promotional)),
        newsletter: Boolean(Number(item.newsletter)),
      }
    }));
  },
  async createMany(form: CreteManyHopeForm[]): Promise<void> {
    await axios.post('/hopes/insert', {
      entities: form.map((item) => ({
        fcShopId: Number(item.fcShopId),
        pushShinyabinFlag: item.pushShinyabinFlag ? '1' : '0',
        newstellerFlag: item.newstellerFlag ? '1' : '0',
        prefId: String(item.prefId).padStart(2, '0'),
        cityId1: String(item.cityId1),
        cityId2: item.cityId2 ? String(item.cityId2) : null,
        cityId3: item.cityId3 ? String(item.cityId3) : null,
        estateType: {
          newEstatePrice: item.estateType.newEstatePrice,
          oldEstatePrice: item.estateType.oldEstatePrice,
          newMansionPrice: item.estateType.newMansionPrice,
          oldMansionPrice: item.estateType.oldMansionPrice,
          landPrice: item.estateType.landPrice,
          businessPrice: item.estateType.businessPrice,
        },
        landPrice: item.landPrice ? item.landPrice.map((price) => String(price)) : null,
        newEstatePrice: item.newEstatePrice ? item.newEstatePrice.map((price) => String(price)) : null,
        oldEstatePrice: item.oldEstatePrice ? item.oldEstatePrice.map((price) => String(price)) : null,
        newMansionPrice: item.newMansionPrice ? item.newMansionPrice.map((price) => String(price)) : null,
        oldMansionPrice: item.oldMansionPrice ? item.oldMansionPrice.map((price) => String(price)) : null,
        businessPrice: item.businessPrice ? item.businessPrice.map((price) => String(price)) : null,
        typeId: item.typeId,
      })),
    });
  },
  async update(form: UpdateHopeForm): Promise<void> {
    await axios.put(`/hope/${form.id}`, {
      hopeId: form.id,
      fcShopId: Number(form.fcShopId),
      prefId: String(form.prefId).padStart(2, '0'),
      cityId1: String(form.cityId1),
      cityId2: form.cityId2 ? String(form.cityId2) : null,
      cityId3: form.cityId3 ? String(form.cityId3) : null,
      estateType: {
        newEstatePrice: form.estateType.newEstatePrice,
        oldEstatePrice: form.estateType.oldEstatePrice,
        newMansionPrice: form.estateType.newMansionPrice,
        oldMansionPrice: form.estateType.oldMansionPrice,
        landPrice: form.estateType.landPrice,
        businessPrice: form.estateType.businessPrice,
      },
      landPrice: form.landPrice ? form.landPrice.map((price) => String(price)) : null,
      newEstatePrice: form.newEstatePrice ? form.newEstatePrice.map((price) => String(price)) : null,
      oldEstatePrice: form.oldEstatePrice ? form.oldEstatePrice.map((price) => String(price)) : null,
      newMansionPrice: form.newMansionPrice ? form.newMansionPrice.map((price) => String(price)) : null,
      oldMansionPrice: form.oldMansionPrice ? form.oldMansionPrice.map((price) => String(price)) : null,
      businessPrice: form.businessPrice ? form.businessPrice.map((price) => String(price)) : null,

      shinyabinFlag: form.shinyabinFlag,
      sinyaBinServiceFlag: form.sinyaBinServiceFlag,
      originalShinyabinFlag: null,
      abortReasonTypeId: form.deleteType,
      abortReason: form.deleteReason,
      deleteReasonExplanation: form.deleteReasonExplanation,
      typeId: form.type === 0 || form.type ? form.type : '2',
      deleteReasonTypeId: null,
      deleteReason: form.deleteReason,
      fcShopEstateHideFlag: form.deleteType ? DeletePlaceFlag[form.deleteType] : form.deleteType,
      allowEmailService: form.allowEmailService,
      newHouseService: form.newHouseService,
      emailMagazineService: form.emailMagazineService,
    });
  },
  async updateMany(form: UpdateManyHopeForm[]): Promise<void> {
    await axios.put('/hopes/multi', {
      entities: form.map((item) => ({
        hopeId: item.id,
        fcShopId: item.fcShopId,
        pushShinyabinFlag: item.pushShinyabinFlag ? '1' : '0',
        estateType: {
          newEstatePrice: !!item.newEstatePrice,
          oldEstatePrice: !!item.oldEstatePrice,
          newMansionPrice: !!item.newMansionPrice,
          oldMansionPrice: !!item.oldMansionPrice,
          landPrice: !!item.landPrice,
          businessPrice: !!item.businessPrice,
        },
        landPrice: item.landPrice ? item.landPrice.map((price) => String(price)) : null,
        newEstatePrice: item.newEstatePrice ? item.newEstatePrice.map((price) => String(price)) : null,
        oldEstatePrice: item.oldEstatePrice ? item.oldEstatePrice.map((price) => String(price)) : null,
        newMansionPrice: item.newMansionPrice ? item.newMansionPrice.map((price) => String(price)) : null,
        oldMansionPrice: item.oldMansionPrice ? item.oldMansionPrice.map((price) => String(price)) : null,
        businessPrice: item.businessPrice ? item.businessPrice.map((price) => String(price)) : null,
        typeId: item.typeId,
      })),
    });
  },
};
